import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useDeferredValue,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Loader2,
  ExternalLink,
  X,
  Search,
  ChevronDown,
  Clock,
  ArrowRight,
  ArrowLeft,
  SearchX,
  TrendingUp,
  ShoppingCart,
  Handshake,
  BarChart3,
  DollarSign,
  LineChart,
  ArrowUpRight,
  FileText,
  ShieldAlert,
  Landmark,
  Newspaper,
  type LucideIcon,
} from "lucide-react";

interface Article {
  title: string;
  link: string;
  excerpt: string; // plain-text summary for cards (safe, consistent clamping)
  content: string; // full HTML, rendered only inside the modal
  pubDate: string;
  author: string;
  guid: string;
  tags: string[];
  image: string | null; // first non-tracking image, extracted once at parse time
  readingMinutes: number;
}

const RSS_URL = "https://resinsmart.ai/articles/rss.xml";
const WORDS_PER_MINUTE = 200;

/* ------------------------------------------------------------------ */
/* Parsing helpers                                                     */
/* ------------------------------------------------------------------ */

function parseRSS(xmlText: string): Article[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "application/xml");
  if (doc.querySelector("parsererror")) throw new Error("XML parse error");

  return Array.from(doc.querySelectorAll("item")).map((item) => {
    const content =
      item.getElementsByTagNameNS(
        "http://purl.org/rss/1.0/modules/content/",
        "encoded"
      )[0]?.textContent ?? "";

    const author = extractAuthorName(item);

    // <link> sits between nodes; use textContent on the first <link> child.
    const linkNode = Array.from(item.childNodes).find(
      (n) => n.nodeName === "link"
    );
    const link = linkNode?.textContent?.trim() ?? "";

    // HubSpot emits each blog tag as a <category> element (managed in HubSpot).
    const tags = Array.from(item.querySelectorAll("category"))
      .map((c) => c.textContent?.trim() ?? "")
      .filter(Boolean);

    const rawDescription = item.querySelector("description")?.textContent ?? "";

    return {
      title: item.querySelector("title")?.textContent?.trim() ?? "",
      link,
      excerpt: stripHtml(rawDescription),
      content,
      pubDate: item.querySelector("pubDate")?.textContent?.trim() ?? "",
      author,
      guid: item.querySelector("guid")?.textContent?.trim() ?? "",
      tags,
      image: extractImage(content) ?? extractImage(rawDescription),
      readingMinutes: Math.max(
        1,
        Math.round(wordCount(stripHtml(content)) / WORDS_PER_MINUTE)
      ),
    };
  });
}

/** Collapse HTML to clean, single-spaced plain text. */
function stripHtml(html: string): string {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || "").replace(/\s+/g, " ").trim();
}

function wordCount(text: string): number {
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

/** RSS <author> reads "email@example.com (Full Name)" — return the name in the parentheses. */
function extractAuthorName(item: Element): string {
  const authorText = item.querySelector("author")?.textContent ?? "";
  const nameInParentheses = authorText.split("(")[1]?.split(")")[0]?.trim();
  return nameInParentheses || "ResinSmart Experts";
}

/** First real content image, skipping tracking pixels and HubSpot beacons. */
function extractImage(html: string): string | null {
  if (!html) return null;
  const div = document.createElement("div");
  div.innerHTML = html;
  for (const img of Array.from(div.querySelectorAll("img"))) {
    const src = img.getAttribute("src") ?? "";
    if (!src) continue;
    const w = img.getAttribute("width");
    const h = img.getAttribute("height");
    if (w === "1" || h === "1") continue;
    if (/__ptq\.gif|track\.hubspot|\/hs\/cta\//i.test(src)) continue;
    return src;
  }
  return null;
}

/* ------------------------------------------------------------------ */
/* Formatting helpers                                                  */
/* ------------------------------------------------------------------ */

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const result = parts.map((w) => w[0]?.toUpperCase() ?? "").join("");
  return result || "RS";
}

/* ------------------------------------------------------------------ */
/* Category cover art                                                  */
/* ------------------------------------------------------------------ */

interface CategoryCoverArt {
  tagMatcher: RegExp;
  CategoryIcon: LucideIcon;
  iconColorClass: string;
  iconColorClassLight: string;
}

const CATEGORY_COVER_ART: CategoryCoverArt[] = [
  { tagMatcher: /pric(e|ing)|index/i, CategoryIcon: TrendingUp, iconColorClass: "text-emerald-300/70", iconColorClassLight: "text-emerald-700" },
  { tagMatcher: /increase/i, CategoryIcon: ArrowUpRight, iconColorClass: "text-rose-300/70", iconColorClassLight: "text-rose-700" },
  { tagMatcher: /procure/i, CategoryIcon: ShoppingCart, iconColorClass: "text-sky-300/70", iconColorClassLight: "text-sky-700" },
  { tagMatcher: /suppl(y|ier).*(risk|chain)|chain/i, CategoryIcon: ShieldAlert, iconColorClass: "text-orange-300/70", iconColorClassLight: "text-orange-700" },
  { tagMatcher: /negotiat|supplier/i, CategoryIcon: Handshake, iconColorClass: "text-amber-300/70", iconColorClassLight: "text-amber-700" },
  { tagMatcher: /intelligence|data/i, CategoryIcon: BarChart3, iconColorClass: "text-indigo-300/70", iconColorClassLight: "text-indigo-700" },
  { tagMatcher: /ebitda|financ/i, CategoryIcon: DollarSign, iconColorClass: "text-teal-300/70", iconColorClassLight: "text-teal-700" },
  { tagMatcher: /signal|forecast/i, CategoryIcon: LineChart, iconColorClass: "text-violet-300/70", iconColorClassLight: "text-violet-700" },
  { tagMatcher: /contract|terms/i, CategoryIcon: FileText, iconColorClass: "text-slate-200/70", iconColorClassLight: "text-slate-600" },
  { tagMatcher: /equity|capital/i, CategoryIcon: Landmark, iconColorClass: "text-fuchsia-300/70", iconColorClassLight: "text-fuchsia-700" },
];

const DEFAULT_COVER_ART: CategoryCoverArt = {
  tagMatcher: /.*/,
  CategoryIcon: Newspaper,
  iconColorClass: "text-rs-green/70",
  iconColorClassLight: "text-emerald-700",
};

function getCategoryCoverArt(articleTags: string[]): CategoryCoverArt {
  for (const articleTag of articleTags) {
    const matchedArt = CATEGORY_COVER_ART.find((art) =>
      art.tagMatcher.test(articleTag)
    );
    if (matchedArt) return matchedArt;
  }
  return DEFAULT_COVER_ART;
}

/* ------------------------------------------------------------------ */
/* Presentational sub-components                                       */
/* ------------------------------------------------------------------ */

const TagPill: React.FC<{
  label: string;
  count?: number;
  active?: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50 ${
      active
        ? "border-rs-green bg-rs-green font-medium text-black"
        : "border-rs-off-white/10 bg-rs-off-black text-rs-off-white/70 hover:border-rs-green/40 hover:text-rs-off-white"
    }`}
  >
    {label}
    {count !== undefined && (
      <span className={active ? "text-black/60" : "text-rs-off-white/40"}>
        {count}
      </span>
    )}
  </button>
);

const CardSkeleton: React.FC = () => (
  <div className="overflow-hidden rounded-xl border border-rs-off-white/5 bg-rs-off-black">
    <div className="h-48 animate-pulse bg-rs-jet/70" />
    <div className="space-y-3 p-6">
      <div className="h-5 w-3/4 animate-pulse rounded bg-rs-jet/70" />
      <div className="h-3 w-1/3 animate-pulse rounded bg-rs-jet/50" />
      <div className="space-y-2 pt-2">
        <div className="h-3 w-full animate-pulse rounded bg-rs-jet/40" />
        <div className="h-3 w-5/6 animate-pulse rounded bg-rs-jet/40" />
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const Articles: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { isDark } = useTheme();

  const [articles, setArticles] = useState<Article[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "most-tagged">(
    "newest"
  );
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  const [selectedAuthors, setSelectedAuthors] = useState<Set<string>>(new Set());
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const authorDropdownRef = useRef<HTMLDivElement>(null);
  const [pageSize, setPageSize] = useState(() =>
    window.matchMedia("(min-width: 1024px)").matches ? 12 : 9
  );

  const deferredSearch = useDeferredValue(searchQuery);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  /* -------------------------------- data ------------------------------- */

  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate("/authorization");
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const res = await fetch(RSS_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setArticles(parseRSS(await res.text()));
      } catch {
        setError("Unable to load articles. Please try again later.");
      } finally {
        setFetching(false);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e: MediaQueryListEvent) => {
      setPageSize(e.matches ? 12 : 9);
      setCurrentPage(1);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [articles, Array.from(selectedTags).sort().join(","), Array.from(selectedAuthors).sort().join(","), deferredSearch, sortBy]);

  useEffect(() => {
    if (!showTagDropdown && !showAuthorDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(e.target as Node)) {
        setShowTagDropdown(false);
      }
      if (authorDropdownRef.current && !authorDropdownRef.current.contains(e.target as Node)) {
        setShowAuthorDropdown(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowTagDropdown(false);
        setShowAuthorDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showTagDropdown, showAuthorDropdown]);

  /* ---------------------------- derived data --------------------------- */

  const allTags = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of articles)
      for (const t of a.tags) counts[t] = (counts[t] ?? 0) + 1;
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [articles]);

  const allAuthors = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of articles) {
      const author = a.author || "ResinSmart Experts";
      counts[author] = (counts[author] ?? 0) + 1;
    }
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [articles]);

  const filteredArticles = useMemo(() => {
    let result = articles;

    if (selectedTags.size > 0) {
      result = result.filter((a) => a.tags.some((t) => selectedTags.has(t)));
    }

    if (selectedAuthors.size > 0) {
      result = result.filter((a) => {
        const author = a.author || "ResinSmart Experts";
        return selectedAuthors.has(author);
      });
    }

    const q = deferredSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    const sorted = [...result];
    if (sortBy === "newest")
      sorted.sort(
        (a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime()
      );
    else if (sortBy === "oldest")
      sorted.sort(
        (a, b) => new Date(a.pubDate).getTime() - new Date(b.pubDate).getTime()
      );
    else sorted.sort((a, b) => b.tags.length - a.tags.length);

    return sorted;
  }, [articles, selectedTags, selectedAuthors, deferredSearch, sortBy]);

  const isFiltering = selectedTags.size > 0 || selectedAuthors.size > 0 || deferredSearch.trim();
  // Only surface a featured hero on the default, unfiltered view.
  const featuredArticle = !isFiltering ? filteredArticles[0] ?? null : null;
  const gridArticles = featuredArticle
    ? filteredArticles.slice(1)
    : filteredArticles;

  const totalPages = Math.max(1, Math.ceil(gridArticles.length / pageSize));
  const paginatedArticles = gridArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const relatedArticles = useMemo(() => {
    if (!selectedArticle) return [];
    return articles
      .filter((a) => a.guid !== selectedArticle.guid)
      .map((a) => ({
        article: a,
        shared: a.tags.filter((t) => selectedArticle.tags.includes(t)).length,
      }))
      .filter((x) => x.shared >= 1)
      .sort((a, b) => b.shared - a.shared)
      .slice(0, 3)
      .map((x) => x.article);
  }, [selectedArticle, articles]);

  const clearFilters = () => {
    setSelectedTags(new Set());
    setSelectedAuthors(new Set());
    setSearchQuery("");
  };

  const toggleAuthorFilter = (author: string) => {
    const newAuthors = new Set(selectedAuthors);
    if (newAuthors.has(author)) {
      newAuthors.delete(author);
    } else {
      newAuthors.add(author);
    }
    setSelectedAuthors(newAuthors);
  };

  const handleAuthorFilterClick = (author: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleAuthorFilter(author);
  };

  const handleAuthorFilterKeyDown = (author: string) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      toggleAuthorFilter(author);
    }
  };

  /* ------------------------------- modal ------------------------------- */

  // Rewrite links + strip tracking pixels inside rendered article HTML.
  useEffect(() => {
    if (!selectedArticle || !modalContentRef.current) return;
    const el = modalContentRef.current;
    el.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    });
    el.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
      const w = img.getAttribute("width");
      const h = img.getAttribute("height");
      if (w === "1" || h === "1") img.style.display = "none";
    });
    el.scrollTop = 0;
  }, [selectedArticle]);

  // Escape-to-close, body scroll-lock, and focus management for the modal.
  useEffect(() => {
    if (!selectedArticle) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedArticle(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [selectedArticle]);

  /* ------------------------------ render ------------------------------- */

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rs-black">
        <Loader2 className="h-16 w-16 animate-spin text-rs-green opacity-30" />
      </div>
    );
  }
  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-rs-black">
      <main className="mx-auto max-w-[1750px] overflow-y-auto px-6 py-10 lg:px-10">
        {/* Masthead */}
        <header className="mb-8 border-b border-rs-off-white/10 pb-8">
          <h1 className="text-4xl font-semibold tracking-tight text-rs-off-white lg:text-5xl">
            Articles
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-rs-off-white/60">
            Resin market insights, trends, and analysis from the ResinSmart
            experts.
          </p>
        </header>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-900/20 p-6 text-red-400">
            {error}
          </div>
        )}

        {!error && (
          <>
            {/* Sticky toolbar: search + sort */}
            <div className="sticky top-0 z-20 -mx-6 mb-6 border-b border-rs-off-white/5 bg-rs-black/80 px-6 py-4 backdrop-blur-md lg:-mx-10 lg:px-10">
              <div className="w-full sm:w-fit">
              <div className="relative w-full">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-rs-off-white/40" />
                <input
                  type="text"
                  aria-label="Search articles"
                  placeholder="Search articles"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-[calc(100%-0.5rem)] rounded-lg border border-rs-off-white/10 bg-rs-off-black py-2 pl-10 pr-9 text-sm text-rs-off-white placeholder-rs-off-white/40 transition-colors focus:border-rs-green/40 focus:outline-none focus:ring-1 focus:ring-rs-green/20"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-rs-off-white/40 hover:text-rs-off-white"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Sort + topic filter dropdowns */}
              {allTags.length > 0 && (
                <div className="relative mt-3 flex flex-wrap items-center gap-2">
                  <div className="relative">
                    <select
                      aria-label="Sort articles"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                      className="cursor-pointer appearance-none rounded-lg border border-rs-off-white/10 bg-rs-off-black py-2.5 pl-4 pr-10 text-sm text-rs-off-white transition-colors focus:border-rs-green/40 focus:outline-none focus:ring-1 focus:ring-rs-green/20"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                      <option value="most-tagged">Most tagged</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-rs-off-white/40" />
                  </div>

                  <div className="relative">
                    <button
                      onClick={() => setShowTagDropdown(!showTagDropdown)}
                      className="flex items-center gap-4 rounded-lg border border-rs-off-white/10 bg-rs-off-black px-4 py-2.5 text-sm text-rs-off-white transition-colors hover:border-rs-green/40 hover:text-rs-green focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50"
                    >
                      <span>
                        {selectedTags.size === 0
                          ? "Filter by topic"
                          : selectedTags.size === 1
                            ? Array.from(selectedTags)[0]
                            : `${selectedTags.size} selected`}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showTagDropdown ? "rotate-180" : ""}`} />
                    </button>

                    {showTagDropdown && (
                      <div
                        ref={tagDropdownRef}
                        className="absolute top-full left-0 z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-rs-off-white/10 bg-rs-off-black shadow-lg"
                      >
                        {selectedTags.size > 0 && (
                          <>
                            <button
                              onClick={() => setSelectedTags(new Set())}
                              className="flex w-full items-center px-4 py-2.5 text-left text-xs font-semibold text-rs-green hover:bg-rs-jet/30"
                            >
                              Clear all filters
                            </button>
                            <div className="border-t border-rs-off-white/5" />
                          </>
                        )}
                        {allTags.map((tag) => {
                          const isChecked = selectedTags.has(tag);
                          return (
                            <button
                              key={tag}
                              onClick={() => {
                                const newTags = new Set(selectedTags);
                                if (isChecked) {
                                  newTags.delete(tag);
                                } else {
                                  newTags.add(tag);
                                }
                                setSelectedTags(newTags);
                              }}
                              className="px-4 py-2.5 text-left text-sm hover:bg-rs-jet/30"
                            >
                              <span className={isChecked ? `font-medium ${isDark ? "text-rs-green" : "text-black"}` : "text-rs-off-white/80"}>
                                {tag}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="relative">
                    <button
                      onClick={() => setShowAuthorDropdown(!showAuthorDropdown)}
                      className="flex items-center gap-4 rounded-lg border border-rs-off-white/10 bg-rs-off-black px-4 py-2.5 text-sm text-rs-off-white transition-colors hover:border-rs-green/40 hover:text-rs-green focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50"
                    >
                      <span>
                        {selectedAuthors.size === 0
                          ? "Filter by author"
                          : selectedAuthors.size === 1
                            ? Array.from(selectedAuthors)[0]
                            : `${selectedAuthors.size} selected`}
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${showAuthorDropdown ? "rotate-180" : ""}`} />
                    </button>

                    {showAuthorDropdown && (
                      <div
                        ref={authorDropdownRef}
                        className="absolute top-full left-0 z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded-lg border border-rs-off-white/10 bg-rs-off-black shadow-lg"
                      >
                        {selectedAuthors.size > 0 && (
                          <>
                            <button
                              onClick={() => setSelectedAuthors(new Set())}
                              className="flex w-full items-center px-4 py-2.5 text-left text-xs font-semibold text-rs-green hover:bg-rs-jet/30"
                            >
                              Clear all filters
                            </button>
                            <div className="border-t border-rs-off-white/5" />
                          </>
                        )}
                        {allAuthors.map((author) => {
                          const isChecked = selectedAuthors.has(author);
                          return (
                            <button
                              key={author}
                              onClick={() => {
                                const newAuthors = new Set(selectedAuthors);
                                if (isChecked) {
                                  newAuthors.delete(author);
                                } else {
                                  newAuthors.add(author);
                                }
                                setSelectedAuthors(newAuthors);
                              }}
                              className="px-4 py-2.5 text-left text-sm hover:bg-rs-jet/30 w-full"
                            >
                              <span className={isChecked ? `font-medium ${isDark ? "text-rs-green" : "text-black"}` : "text-rs-off-white/80"}>
                                {author}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Loading skeletons */}
            {fetching && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            )}

            {/* Featured hero */}
            {!fetching && featuredArticle && (
              <article
                role="button"
                tabIndex={0}
                onClick={() => setSelectedArticle(featuredArticle)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelectedArticle(featuredArticle);
                  }
                }}
                className="group relative mb-12 flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-rs-off-white/10 bg-rs-off-black p-8 transition-colors duration-300 hover:border-rs-green/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50 lg:flex-row lg:items-center lg:justify-between lg:gap-8 lg:p-12"
              >
                <span className="absolute inset-y-0 left-0 w-1 bg-rs-green" />

                <div className="flex flex-1 flex-col">
                  <div className="mb-5 flex items-center gap-3">
                    <span className="inline-flex items-center rounded-full bg-rs-green px-3 py-1 text-xs font-semibold uppercase tracking-wider text-black">
                      Featured
                    </span>
                    {featuredArticle.tags[0] && (
                      <span
                        className={`text-xs font-semibold uppercase tracking-wider ${isDark ? getCategoryCoverArt(featuredArticle.tags).iconColorClass : getCategoryCoverArt(featuredArticle.tags).iconColorClassLight}`}
                      >
                        {featuredArticle.tags[0]}
                      </span>
                    )}
                  </div>

                  <h2 className={`max-w-4xl text-3xl font-semibold leading-tight tracking-tight transition-colors duration-300 lg:text-5xl ${isDark ? "text-rs-off-white group-hover:text-rs-green" : "text-black group-hover:text-black/50"}`}>
                    {featuredArticle.title}
                  </h2>

                  <p className="mt-5 max-w-3xl line-clamp-3 text-lg text-rs-off-white/60">
                    {featuredArticle.excerpt}
                  </p>
                </div>

                <div className="mt-8 flex flex-shrink-0 items-center gap-3 text-sm text-rs-off-white/50 lg:mt-0">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleAuthorFilterClick(featuredArticle.author || "ResinSmart Experts")}
                    onKeyDown={handleAuthorFilterKeyDown(featuredArticle.author || "ResinSmart Experts")}
                    className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-rs-green text-xs font-semibold text-black hover:opacity-80 transition-opacity"
                  >
                    {initials(featuredArticle.author)}
                  </span>
                  <div className="flex flex-col">
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={handleAuthorFilterClick(featuredArticle.author || "ResinSmart Experts")}
                      onKeyDown={handleAuthorFilterKeyDown(featuredArticle.author || "ResinSmart Experts")}
                      className={`cursor-pointer font-semibold transition-colors hover:text-rs-green ${
                        selectedAuthors.has(featuredArticle.author || "ResinSmart Experts")
                          ? "text-rs-green"
                          : isDark ? "text-rs-off-white" : "text-black"
                      }`}
                    >
                      {featuredArticle.author || "ResinSmart Experts"}
                    </span>
                    <span className="flex items-center gap-2 text-xs">
                      {formatDate(featuredArticle.pubDate)}
                      <span className="text-rs-off-white/20">·</span>
                      <Clock className="h-3 w-3" />
                      {featuredArticle.readingMinutes} min read
                    </span>
                  </div>
                </div>
              </article>
            )}

            {/* Result count */}
            {!fetching && isFiltering && filteredArticles.length > 0 && (
              <p className="mb-6 text-sm text-rs-off-white/50">
                {filteredArticles.length}{" "}
                {filteredArticles.length === 1 ? "article" : "articles"}
                {selectedAuthors.size > 0 && (
                  <>
                    {" "}
                    by{" "}
                    <span className="text-rs-off-white/80">
                      {Array.from(selectedAuthors).join(", ")}
                    </span>
                  </>
                )}
                {selectedTags.size > 0 && (
                  <>
                    {" "}
                    tagged{" "}
                    <span className="text-rs-off-white/80">
                      {Array.from(selectedTags).join(", ")}
                    </span>
                  </>
                )}
                {deferredSearch.trim() && (
                  <>
                    {" "}
                    matching{" "}
                    <span className="text-rs-off-white/80">
                      "{deferredSearch.trim()}"
                    </span>
                  </>
                )}
              </p>
            )}

            {/* Empty state */}
            {!fetching && filteredArticles.length === 0 && (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-rs-off-white/10 py-24 text-center">
                <SearchX className="mb-4 h-10 w-10 text-rs-off-white/30" />
                <p className="text-rs-off-white/70">
                  No articles match your filters.
                </p>
                {isFiltering && (
                  <button
                    onClick={clearFilters}
                    className="mt-4 rounded-lg border border-rs-green/40 px-4 py-2 text-sm font-medium text-rs-green transition-colors hover:bg-rs-green/10"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )}

            {/* Grid */}
            {!fetching && paginatedArticles.length > 0 && (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {paginatedArticles.map((article, index) => (
                  <article
                    key={article.guid || index}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedArticle(article)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelectedArticle(article);
                      }
                    }}
                    className="group relative flex cursor-pointer flex-col overflow-hidden rounded-xl border border-rs-off-white/7 bg-rs-off-black p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-rs-green/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50"
                  >
                    <span className="absolute inset-y-0 left-0 w-0.5 bg-rs-green opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

                    {article.tags[0] && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTags = new Set(selectedTags);
                          if (newTags.has(article.tags[0])) {
                            newTags.delete(article.tags[0]);
                          } else {
                            newTags.add(article.tags[0]);
                          }
                          setSelectedTags(newTags);
                        }}
                        className={`mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${isDark ? getCategoryCoverArt(article.tags).iconColorClass : getCategoryCoverArt(article.tags).iconColorClassLight}`}
                      >
                        <span className="h-3.5 w-1 rounded-full bg-rs-green" />
                        {article.tags[0]}
                      </button>
                    )}

                    <h3 className={`text-xl font-semibold leading-snug tracking-tight transition-colors duration-300 lg:text-2xl ${isDark ? "text-rs-off-white group-hover:text-rs-green" : "text-black group-hover:text-black/50"}`}>
                      {article.title}
                    </h3>

                    <p className="mt-3 line-clamp-3 flex-1 text-sm text-rs-off-white/60">
                      {article.excerpt}
                    </p>

                    {article.tags.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {article.tags.map((tag) => {
                          const isActive = selectedTags.has(tag);
                          return (
                            <button
                              key={tag}
                              onClick={(e) => {
                                e.stopPropagation();
                                const newTags = new Set(selectedTags);
                                if (newTags.has(tag)) {
                                  newTags.delete(tag);
                                } else {
                                  newTags.add(tag);
                                }
                                setSelectedTags(newTags);
                              }}
                              className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                                isActive
                                  ? "border-rs-green bg-rs-green text-black"
                                  : isDark
                                    ? "border-rs-off-white/15 text-rs-off-white/60 hover:border-rs-green/50 hover:text-rs-green"
                                    : "border-rs-off-white/15 text-rs-off-white/60 hover:border-rs-green/50 hover:text-black"
                              }`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-5 flex items-center gap-2 text-xs text-rs-off-white/50">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={handleAuthorFilterClick(article.author || "ResinSmart Experts")}
                        onKeyDown={handleAuthorFilterKeyDown(article.author || "ResinSmart Experts")}
                        className={`cursor-pointer font-semibold transition-colors hover:text-rs-green ${
                          selectedAuthors.has(article.author || "ResinSmart Experts")
                            ? "text-rs-green"
                            : isDark ? "text-rs-off-white" : "text-black"
                        }`}
                      >
                        {article.author || "ResinSmart Experts"}
                      </span>
                      <span className="text-rs-off-white/20">·</span>
                      <span>{formatDate(article.pubDate)}</span>
                      <span className="text-rs-off-white/20">·</span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {article.readingMinutes} min
                      </span>
                    </div>

                    <div className={`mt-4 flex items-center gap-1.5 text-sm font-semibold ${isDark ? "text-rs-green" : "text-black"}`}>
                      Read more
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!fetching && totalPages > 1 && (
              <div className="mt-14 flex items-center justify-center gap-6 pb-8">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  className="flex items-center justify-center p-2.5 text-rs-off-white transition-colors hover:text-rs-green disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <span className="text-sm text-rs-off-white/60">
                  Page{" "}
                  <span className="font-semibold text-rs-off-white">
                    {currentPage}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-rs-off-white">
                    {totalPages}
                  </span>
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  className="flex items-center justify-center p-2.5 text-rs-off-white transition-colors hover:text-rs-green disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Article modal */}
      {selectedArticle && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={selectedArticle.title}
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedArticle(null);
          }}
        >
          <div className="mx-auto my-8 flex w-full max-w-3xl flex-col rounded-2xl border border-rs-jet-light/55 bg-rs-off-black shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-rs-jet-light/55 p-6">
              <div className="flex-1">
                {selectedArticle.tags[0] && (
                  <span className={`mb-2 inline-block text-xs font-semibold uppercase tracking-wider ${isDark ? "text-rs-green" : "text-black"}`}>
                    {selectedArticle.tags[0]}
                  </span>
                )}
                <h2 className={`text-2xl font-semibold leading-tight tracking-tight ${isDark ? "text-rs-off-white" : "text-black"}`}>
                  {selectedArticle.title}
                </h2>
                <div className="mt-3 flex items-center gap-3 text-sm text-rs-off-white/50">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-rs-green text-xs font-semibold text-black">
                    {initials(selectedArticle.author)}
                  </span>
                  <span className="text-rs-off-white/80">
                    {selectedArticle.author || "ResinSmart"}
                  </span>
                  <span className="text-rs-off-white/20">·</span>
                  <span>{formatDate(selectedArticle.pubDate)}</span>
                  <span className="text-rs-off-white/20">·</span>
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {selectedArticle.readingMinutes} min read
                  </span>
                </div>
              </div>
              <button
                ref={closeButtonRef}
                onClick={() => setSelectedArticle(null)}
                aria-label="Close article"
                className="flex-shrink-0 rounded-lg p-1 text-rs-off-white/50 transition-colors hover:bg-rs-jet hover:text-rs-off-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rs-green/50"
              >
                <X size={22} />
              </button>
            </div>

            {/* Body */}
            <div
              ref={modalContentRef}
              className="article-content max-h-[55vh] overflow-y-auto p-6"
              style={{ color: isDark ? "#f5f5f4" : "black" }}
              dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
            />

            {/* Related */}
            {relatedArticles.length > 0 && (
              <div className="border-t border-rs-jet-light/55 p-6">
                <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-rs-off-white/50">
                  Related articles
                </h4>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {relatedArticles.map((rel) => (
                    <button
                      key={rel.guid}
                      onClick={() => setSelectedArticle(rel)}
                      className="group flex flex-col gap-2 rounded-lg border border-rs-off-white/5 bg-rs-black/40 p-3 text-left transition-colors duration-200 hover:border-rs-green/30 hover:bg-rs-black/60"
                    >
                      <p className={`line-clamp-2 text-sm font-medium text-rs-off-white/80 transition-colors ${isDark ? "group-hover:text-rs-green" : "group-hover:text-rs-off-white/50"}`}>
                        {rel.title}
                      </p>
                      <p className="text-xs text-rs-off-white/40">
                        {formatDate(rel.pubDate)} · {rel.readingMinutes} min
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-rs-jet-light/55 p-6">
              <a
                href={selectedArticle.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 rounded-lg transition-colors ${isDark ? "bg-rs-green/20 text-rs-green hover:bg-rs-green/30" : "bg-rs-green text-black hover:bg-rs-green/90"}`}
                style={{ padding: "0.625rem 1.5rem" }}
              >
                <ExternalLink size={16} />
                Read on ResinSmart.ai
              </a>
              <button
                onClick={() => setSelectedArticle(null)}
                className="rounded-lg border border-rs-off-white/10 px-6 py-2.5 text-rs-off-white transition-colors hover:border-rs-green/40 hover:text-rs-green"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articles;
